import React, { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useFeatureFlags } from '@node-space/hooks'
import { RadioProps } from '@node-space/storybook-components/dist/components/Radio'
import { setAdressFieldErrors } from 'components/AddressFormFields/utils'
import { IdVerificationMessage } from 'components/IdVerification/IdVerificationMessage'
import { ConfirmRadioValues } from 'constants/Forms'
import { useCountriesContext } from 'contexts/countriesContext'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import { ApiErrorResponse } from 'types/errors'
import { AccountContactFields } from './AccountContactFields'
import { AccountContactWrapper } from './AccountContactWrapper'
import { PersonDefaultOptions } from './helpers/constants'
import {
  AccountContactActionsData,
  AccountContactData,
  AccountContactFormData,
  AccountContactLabels,
  onSaveAccountContact,
} from './helpers/types'
import { getAccountContactPayload, getErrorFormKey } from './helpers/utils'
import { useAccountContactForm } from './hooks/useAccountContactForm'
import { usePersonSelector } from './hooks/usePersonSelector'
import { PersonSelector } from './PersonSelector'

export interface AccountContactFormProps extends AccountContactActionsData {
  accountContact: AccountContactData
  labels: AccountContactLabels
  hasTopPadding?: boolean
  onSave: onSaveAccountContact
  onDelete: () => void
}

export const AccountContactForm = ({
  currentContactType,
  accountContact,
  personSelectorData,
  labels,
  idVerificationMessage,
  isUpdatingDetails,
  loading,
  onSave,
  onDelete,
}: AccountContactFormProps) => {
  const { enableDualNationality } = useFeatureFlags()
  const countries = useCountriesContext()
  const { selectedData, personSelectorInfo, onPersonChange } = usePersonSelector(
    personSelectorData,
    currentContactType
  )

  const [hasDateOfBirthErrors, setHasDateOfBirthErrors] = useState<boolean>()

  const { formatDateOfBirthInput } = useDateOfBirthInput()

  const initialAccountContactData =
    !isUpdatingDetails && !!selectedData ? selectedData : accountContact

  const { form, formValues, formFields, formErrors, resetForm, getIsControllerCheckBoxesValid } =
    useAccountContactForm({
      initialAccountContactData,
      countryOptions: countries?.countryOptions,
      contactType: currentContactType,
    })

  const contactFullName = `${formValues?.firstName} ${formValues?.lastName}`

  const handleContinueIdVerificationMessage = () => {
    idVerificationMessage?.onContinue(contactFullName)
  }

  const handleMappedErrors = (error: ApiErrorResponse) => {
    return setAdressFieldErrors(error, getErrorFormKey, form?.setError)
  }

  const onSubmit: SubmitHandler<typeof formValues> = (formValues: AccountContactFormData) => {
    if ((formFields?.isController as RadioProps)?.value === ConfirmRadioValues.YES) {
      const isControllerCheckBoxesValid = getIsControllerCheckBoxesValid()
      if (!isControllerCheckBoxesValid) return
    }
    const isNewPerson = !isUpdatingDetails && !initialAccountContactData?.person?.reference
    const isAddingExistingPerson =
      !isUpdatingDetails && !!initialAccountContactData?.person?.reference
    const accountContactPayload = getAccountContactPayload({
      initialData: initialAccountContactData,
      formValues: { ...formValues, dateOfBirth: formatDateOfBirthInput(formValues.dateOfBirth) },
      isAddingExistingPerson,
      isSecondNationalityEnabled: !!enableDualNationality,
    })
    onSave(accountContactPayload, isNewPerson, handleMappedErrors)
  }

  useEffect(() => {
    !isUpdatingDetails && resetForm()
  }, [isUpdatingDetails, selectedData])

  return idVerificationMessage?.showMessage ? (
    <IdVerificationMessage
      contactType={currentContactType}
      {...(personSelectorInfo?.selectedReference !== PersonDefaultOptions.MYSELF && {
        contactFullName,
      })}
      linkUrl={idVerificationMessage?.linkUrl}
      onClose={handleContinueIdVerificationMessage}
      onContinue={handleContinueIdVerificationMessage}
    />
  ) : (
    <AccountContactWrapper
      addActionLabel={labels?.addButton}
      actionsLoading={loading}
      actionsError={{
        isFormValuesError: !!Object.keys(formErrors)?.length || hasDateOfBirthErrors,
      }}
      isEditing={isUpdatingDetails}
      onSubmit={form?.handleSubmit(onSubmit)}
      onDelete={onDelete}
    >
      {!isUpdatingDetails && (
        <PersonSelector {...personSelectorInfo} onPersonChange={onPersonChange} />
      )}

      <AccountContactFields
        contactType={currentContactType}
        savedData={initialAccountContactData}
        form={form}
        formFields={formFields}
        formValues={formValues}
        formErrors={formErrors}
        isEditingContact={isUpdatingDetails}
        setIsDateOfBirthError={setHasDateOfBirthErrors}
      />
    </AccountContactWrapper>
  )
}
